import React, { useEffect, useState, useMemo } from "react";
import { Cookie, X } from "lucide-react";
import { getI18n } from "../lib/i18n";

const STORAGE_KEY = "mediquizit_cookie_consent_v1";

function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_e) {
    return null;
  }
}

function saveConsent(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      ...state,
      ts: new Date().toISOString(),
      v: 1,
    }));
  } catch (_e) {
    // ignore
  }
}

/**
 * EU-compliant cookie banner with 4 categories.
 * - Necessary cookies are always on (locked toggle).
 * - User can Accept All / Reject Non-essential / Configure.
 * - State persisted in localStorage so the banner won't reappear.
 * - All strings flip with the `language` prop (it/es).
 */
export default function CookieBanner({ language = "it" }) {
  const t = getI18n(language).cookies;
  const [open, setOpen] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [prefs, setPrefs] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false,
  });

  useEffect(() => {
    const existing = readConsent();
    if (!existing) setOpen(true);
    else if (existing.prefs) setPrefs((p) => ({ ...p, ...existing.prefs }));
  }, []);

  const acceptAll = () => {
    const next = { necessary: true, analytics: true, marketing: true, functional: true };
    setPrefs(next);
    saveConsent({ choice: "accept_all", prefs: next });
    setOpen(false);
  };
  const rejectNonEssential = () => {
    const next = { necessary: true, analytics: false, marketing: false, functional: false };
    setPrefs(next);
    saveConsent({ choice: "reject_non_essential", prefs: next });
    setOpen(false);
  };
  const savePrefs = () => {
    saveConsent({ choice: "custom", prefs });
    setOpen(false);
  };

  const cats = useMemo(() => ([
    { key: "necessary", title: t.cat_necessary, desc: t.cat_necessary_desc, locked: true },
    { key: "analytics", title: t.cat_analytics, desc: t.cat_analytics_desc },
    { key: "marketing", title: t.cat_marketing, desc: t.cat_marketing_desc },
    { key: "functional", title: t.cat_functional, desc: t.cat_functional_desc },
  ]), [t]);

  if (!open) return null;

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-label="Cookie consent"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 z-[9999] max-w-md mx-auto sm:mx-0"
    >
      <div
        className="rounded-3xl shadow-2xl backdrop-blur-xl overflow-hidden"
        style={{
          background: "linear-gradient(135deg, rgba(255,255,255,0.97), rgba(248,255,250,0.97))",
          border: "1px solid rgba(0,140,69,0.25)",
          boxShadow: "0 25px 60px -20px rgba(0,80,40,0.35)",
        }}
      >
        {/* Italian flag accent bar — top */}
        <div className="h-1 w-full flex" aria-hidden="true">
          <div className="flex-1" style={{ background: "#008C45" }} />
          <div className="flex-1" style={{ background: "#FFFFFF" }} />
          <div className="flex-1" style={{ background: "#CD212A" }} />
        </div>
        <div className="p-5">
        {!showConfig ? (
          <>
            <div className="flex items-start gap-3 mb-3">
              <div className="h-9 w-9 rounded-xl grid place-items-center shrink-0"
                   style={{ background: "rgba(0,140,69,0.12)" }}>
                <Cookie className="h-5 w-5" style={{ color: "#008C45" }} />
              </div>
              <div>
                <h3 className="font-semibold text-[15px]" style={{ color: "#1A3A22" }}>
                  {t.title}
                </h3>
                <p className="text-[13px] mt-1 leading-relaxed" style={{ color: "#3A4A40" }}>
                  {t.body}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4">
              <button
                data-testid="cookie-accept-all"
                onClick={acceptAll}
                className="px-3 py-2.5 rounded-xl text-[13px] font-semibold transition-transform hover:scale-[1.02]"
                style={{ background: "linear-gradient(135deg, #00A052, #008C45)", color: "white",
                         boxShadow: "0 4px 14px -4px rgba(0,140,69,0.5)" }}
              >
                {t.accept_all}
              </button>
              <button
                data-testid="cookie-reject"
                onClick={rejectNonEssential}
                className="px-3 py-2.5 rounded-xl text-[13px] font-medium border"
                style={{ borderColor: "rgba(205,33,42,0.45)", color: "#CD212A", background: "white" }}
              >
                {t.reject_all}
              </button>
              <button
                data-testid="cookie-configure"
                onClick={() => setShowConfig(true)}
                className="px-3 py-2.5 rounded-xl text-[13px] font-medium"
                style={{ color: "#008C45", textDecoration: "underline" }}
              >
                {t.configure}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-[15px]" style={{ color: "#1A3A22" }}>
                {t.configure}
              </h3>
              <button onClick={() => setShowConfig(false)}
                      data-testid="cookie-config-close"
                      aria-label="Close" className="p-1 rounded-md"
                      style={{ color: "#008C45" }}>
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
              {cats.map((c) => (
                <div key={c.key} className="flex items-start justify-between gap-3 p-3 rounded-xl"
                     style={{ background: "rgba(248,255,250,0.7)",
                              border: "1px solid rgba(0,140,69,0.18)" }}>
                  <div className="min-w-0">
                    <p className="text-[13px] font-medium" style={{ color: "#1A3A22" }}>{c.title}</p>
                    <p className="text-[11px] mt-0.5 leading-relaxed" style={{ color: "#3A4A40" }}>{c.desc}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer shrink-0">
                    <input
                      type="checkbox"
                      disabled={c.locked}
                      checked={c.locked ? true : !!prefs[c.key]}
                      onChange={(e) => setPrefs({ ...prefs, [c.key]: e.target.checked })}
                      className="sr-only peer"
                      data-testid={`cookie-toggle-${c.key}`}
                    />
                    <div className="w-10 h-5 rounded-full transition-colors"
                         style={{
                           background: c.locked
                             ? "rgba(0,140,69,0.6)"
                             : (prefs[c.key] ? "#008C45" : "#D7D9D2"),
                           opacity: c.locked ? 0.85 : 1,
                         }}>
                      <div className="h-4 w-4 rounded-full bg-white shadow transition-transform mt-0.5 ml-0.5"
                           style={{
                             transform: (c.locked || prefs[c.key])
                               ? "translateX(20px)" : "translateX(0)",
                           }}/>
                    </div>
                  </label>
                </div>
              ))}
            </div>
            <button
              data-testid="cookie-save-prefs"
              onClick={savePrefs}
              className="w-full mt-4 px-3 py-2.5 rounded-xl text-[13px] font-semibold"
              style={{ background: "linear-gradient(135deg, #00A052, #008C45)", color: "white",
                       boxShadow: "0 4px 14px -4px rgba(0,140,69,0.5)" }}
            >
              {t.save}
            </button>
          </>
        )}
        </div>
      </div>
    </div>
  );
}
